import type {
  BotGrowthRule,
  BotPlayRhythm,
  BotProfileStatRule,
} from "../config/types";
import { EVERY_BOUNDS, onlineMinutesPerWeek } from "./botsValidation";

export type ForecastPeriod = "day" | "week" | "month";

/** How many days each period of the Forecast toggle covers. */
export const PERIOD_DAYS: Record<ForecastPeriod, number> = {
  day: 1,
  week: 7,
  month: 30,
};

/** The midpoint of a whole, forward range, or null when it cannot be saved. */
function mid(min: number, max: number, lo: number, hi: number): number | null {
  if (!Number.isInteger(min) || !Number.isInteger(max)) return null;
  if (min < lo || max > hi || min > max) return null;
  return (min + max) / 2;
}

/**
 * What one bot of a profile gains of the stat in an average week: its online
 * minutes ÷ the mean interval × the mean gain while playing, or the mean gain
 * × 7 once a day. Null when a range the product needs cannot be saved.
 */
export function gainPerWeek(
  rule: BotGrowthRule,
  play: BotPlayRhythm | undefined,
): number | null {
  const gain = mid(rule.gain_min, rule.gain_max, 0, Number.MAX_SAFE_INTEGER);
  if (gain === null) return null;
  if (rule.mode === "per_day") return gain * PERIOD_DAYS.week;
  const every = mid(
    rule.every_minutes_min,
    rule.every_minutes_max,
    EVERY_BOUNDS.lo,
    EVERY_BOUNDS.hi,
  );
  const online = onlineMinutesPerWeek(play);
  if (every === null || online === null) return null;
  return (online / every) * gain;
}

/** The same gain over the period the toggle shows. */
export function gainPerPeriod(perWeek: number, period: ForecastPeriod): number {
  return (perWeek * PERIOD_DAYS[period]) / PERIOD_DAYS.week;
}

/**
 * Where a fresh bot's stat starts: the midpoint of its card rule's `start`,
 * or null when the card gives the key no range — the server never creates a
 * key a generated bot does not carry, so such a bot never grows it.
 */
export function startMean(
  stats: BotProfileStatRule[],
  key: string,
): number | null {
  const rule = stats.find((r) => r.key === key);
  if (rule?.start_min === undefined || rule.start_max === undefined) return null;
  return (rule.start_min + rule.start_max) / 2;
}

/**
 * Weeks until a bot starting at `start` reaches the cap — 0 when it starts
 * there already — or null when there is no cap, the stat does not move, or it
 * takes a year or more.
 */
export function weeksToCap(
  cap: number | undefined,
  start: number,
  perWeek: number | null,
): number | null {
  if (cap === undefined || !Number.isFinite(cap)) return null;
  if (start >= cap) return 0;
  if (perWeek === null || perWeek <= 0) return null;
  const weeks = (cap - start) / perWeek;
  return weeks * PERIOD_DAYS.week < 365 ? weeks : null;
}

/** «cap in ~7 wk», or «at cap» for a bot that starts there. */
export function capLabel(weeks: number): string {
  if (weeks <= 0) return "at cap";
  return `cap in ~${Math.max(1, Math.round(weeks))} wk`;
}

/** «+6», «+1.4k», «+16.5k», «+2.1M». */
export function formatGain(n: number): string {
  const r = Math.round(n);
  if (r < 1_000) return `+${r}`;
  // The unit is chosen after rounding, so 999 960 reads «+1M», not «+1000k».
  const tenths = (unit: number) => Math.round(n / (unit / 10)) / 10;
  const short = (v: number, unit: string) =>
    `+${v.toFixed(1).replace(/\.0$/, "")}${unit}`;
  const k = tenths(1_000);
  if (k < 1_000) return short(k, "k");
  return short(tenths(1_000_000), "M");
}
